import { currencyFormatter } from "@common/helpers/currency";
import { FinancialAccount } from "@common/types/financialAccount";
import { FinancialAccountOpeningBalanceModal } from "@components/financialAccount/FinancialAccountOpeningBalanceModal/FinancialAccountOpeningBalanceModal";
import { Button } from "@design-system/Button/Button";
import { StandardModal } from "@design-system/Modal/Modal";
import { useCallback, useState } from "react";
import { useAccounts } from "@hooks/useAccounts";
import { useOpeningBalance } from "@api/openingBalance.api";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import styles from "./Reconciliation.module.scss";

interface Props {
    openingBalance: number | null;
    financialAccount: FinancialAccount;
    suggestedOpeningBalance?: number;
    isEditable?: boolean;
    onOpeningBalanceUpdated: (
        openingBalance: number | null,
    ) => Promise<void> | void;
}

export const ReconciliationOpeningBalance: React.FC<Props> = ({
    openingBalance,
    financialAccount,
    suggestedOpeningBalance,
    isEditable,
    onOpeningBalanceUpdated,
}) => {
    const [openModal, setOpenModal] = useState(false);
    const { accounts } = useAccounts({
        entityId: financialAccount.entityId ?? undefined,
        ledgerBasis: AccountingLedgerBasis.cash,
    });
    const account = accounts.find(
        (coaAccount) => coaAccount.financialAccountId === financialAccount.id,
    );
    const { data: openingBalanceState } = useOpeningBalance(
        financialAccount.entityId ?? undefined,
        account?.id,
    );
    const hasOpeningBalance = openingBalanceState?.openingBalance != null;

    const handleClose = useCallback(
        async (newOpeningBalance?: number | null) => {
            if (newOpeningBalance !== undefined) {
                await onOpeningBalanceUpdated(newOpeningBalance);
            }
            setOpenModal(false);
        },
        [onOpeningBalanceUpdated],
    );

    return (
        <div className={styles.openingBalance}>
            {isEditable && (
                <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setOpenModal(true)}
                    className={styles.setOpeningBalanceButton}
                >
                    {hasOpeningBalance ? "Edit" : "Set opening balance"}
                </Button>
            )}
            {openingBalance !== null &&
                currencyFormatter.format(openingBalance)}
            <StandardModal
                show={openModal}
                onHide={() => setOpenModal(false)}
                size="xl"
            >
                <FinancialAccountOpeningBalanceModal
                    financialAccount={financialAccount}
                    suggestedOpeningBalance={suggestedOpeningBalance}
                    onClose={handleClose}
                />
            </StandardModal>
        </div>
    );
};
